// client/src/pages/seo/ManageMenuOnlineForRestaurantsEn.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SeoRelatedLinks from "../../components/seo/SeoRelatedLinks.jsx";
import SeoHeader from "../../components/seo/SeoHeader.jsx";
import SeoContactSection from "../../components/seo/SeoContactSection.jsx";
import SeoWorkSteps from "../../components/seo/SeoWorkSteps.jsx";

const sections = [
  {
    id: "check",
    label: "Does your restaurant need online menu management?",
  },
  {
    id: "definition",
    label: "What does it mean to manage a menu online?",
  },
  {
    id: "costs",
    label: "Why fast price updates matter for restaurants",
  },
  {
    id: "availability",
    label: "Hide dishes when they are not available",
  },
  {
    id: "daily-menu",
    label: "Publish daily menus and seasonal offers faster",
  },
  {
    id: "photos",
    label: "Improve photos and descriptions directly",
  },
  {
    id: "pdf",
    label: "Why PDF menus are difficult when changes happen often",
  },
  {
    id: "workflow",
    label: "What a good menu management workflow looks like",
  },
  {
    id: "mistakes",
    label: "Common mistakes in online menu management",
  },
  {
    id: "bestplace",
    label: "How Best Place helps manage menus online",
  },
  {
    id: "faq",
    label: "FAQ about online menu management",
  },
];

const checklist = [
  "You regularly change prices or daily offers.",
  "Some dishes are temporarily sold out.",
  "You want to hide products quickly and show them again later.",
  "You want to edit photos, descriptions, or categories yourself.",
  "You do not want to create a new PDF for every change.",
  "You want to depend less on printers, designers, or developers.",
  "Guests already check your menu online before visiting.",
  "You want to publish seasonal offers faster.",
  "Your team should work with current menu information.",
];

const CALLBACK_API_ORIGIN =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1")
    ? "http://localhost:3030"
    : "";

function getResult(score) {
  if (score <= 3) {
    return {
      title: "Online menu management may not be urgent yet",
      text:
        "If your menu rarely changes, a simple digital version may be enough for now. Still, easier updates can become useful over time.",
      tone: "low",
    };
  }

  if (score <= 6) {
    return {
      title: "Online menu management is useful",
      text:
        "Your restaurant could benefit from changing prices, availability, photos, and daily offers faster without rebuilding files or printing again.",
      tone: "medium",
    };
  }

  return {
    title: "Online menu management can strongly help daily operations",
    text:
      "Several points suggest that your restaurant needs an editable online menu: current prices, hidden sold-out dishes, daily offers, photos, and less effort for updates.",
    tone: "high",
  };
}

function VisualIdea({ children }) {
  return (
    <div className="seoVisualIdea" aria-label="Visual idea">
      <div className="seoVisualIdea__icon" aria-hidden="true">
        ✦
      </div>
      <div>
        <strong>Idea for illustration:</strong>
        <p>{children}</p>
      </div>
    </div>
  );
}

export default function ManageMenuOnlineForRestaurantsEn() {
  const [checked, setChecked] = useState([]);

  const score = checked.length;
  const result = useMemo(() => getResult(score), [score]);

  useEffect(() => {
    document.documentElement.setAttribute("lang", "en");
    document.title =
      "Manage Menu Online for Restaurants: Prices, Offers and Availability | Best Place";
  }, []);

  const toggle = (item) => {
    setChecked((prev) =>
      prev.includes(item) ? prev.filter((x) => x !== item) : [...prev, item]
    );
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <main className="qrArticle">
      <SeoHeader
        lang="en"
        langLinks={{
          en: "/en/manage-menu-online-for-restaurants",
          de: "/de/speisekarte-online-verwalten",
          no: "/no/administrere-meny-online-for-restauranter",
        }}
      />

      <section className="qrArticle__hero">
        <div className="qrArticle__heroInner">
          <h1 className="qrArticle__heroTitle">
            <span>
              Manage your restaurant menu online: update prices, daily offers
              and availability faster
            </span>
          </h1>

          <div className="qrArticle__topsection">
            <div className="qrArticle__topsection--img-wrapper">
              <div className="qrArticle__topsection--img"></div>
            </div>

            <div className="qrArticle__topsection--text-wrapper">
              <p className="seoHero__lead">
                An editable online menu helps restaurants react faster: update
                prices, hide sold-out dishes, publish daily offers, and improve
                photos or descriptions without rebuilding a PDF.
              </p>

              <p className="seoHero__text">
                The benefit is not only design. The real benefit appears in
                daily work: when something changes, the restaurant should not
                always need a new file, new print, or a developer.
              </p>
            </div>
          </div>
        </div>
      </section>

      <SeoContactSection lang="en" apiOrigin={CALLBACK_API_ORIGIN} />

      <section className="articls">
        <h2>Content of the article</h2>

        <nav className="qrArticle__nav" aria-label="Article navigation">
          <ol className="qrArticle__navList">
            {sections.map((item, index) => (
              <li key={item.id} className="qrArticle__navItem">
                <button
                  type="button"
                  className="qrArticle__navButton"
                  onClick={() => scrollToSection(item.id)}
                >
                  <span className="qrArticle__navNumber">
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <span className="qrArticle__navLabel">{item.label}</span>
                </button>
              </li>
            ))}
          </ol>
        </nav>
      </section>

      <article>
        <section id="check" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Does your restaurant need online menu management?</h2>

            <p>
              Press the statements that sound true for your restaurant. The
              result is intentionally honest: not every restaurant needs advanced
              menu management from day one, but many restaurants can benefit
              from faster updates much earlier than they think.
            </p>

            <div className="qrArticle__checkGrid">
              {checklist.map((item) => (
                <label key={item} className="qrArticle__checkItem">
                  <input
                    type="checkbox"
                    checked={checked.includes(item)}
                    onChange={() => toggle(item)}
                  />
                  <span className="text">{item}</span>
                </label>
              ))}
            </div>

            <div className={`qrArticle__result qrArticle__result--${result.tone}`}>
              <div className="qrArticle__score">
                {score} / {checklist.length}
              </div>

              <div>
                <strong>{result.title}</strong>
                <p>{result.text}</p>
              </div>
            </div>
          </div>
        </section>

        <section id="definition" className="acquaintance">
          <h2>What does it mean to manage a menu online?</h2>

          <p>
            Managing a menu online means that a restaurant can edit menu content
            through a digital interface: categories, dishes, prices,
            descriptions, photos, and availability.
          </p>

          <p>
            Instead of creating a new file or asking someone for every small
            change, the owner or administrator can update the menu directly.
          </p>

          <ul className="qrArticle__block-li">
            <li className="text__seo-li">Change prices.</li>
            <li className="text__seo-li">Add dishes.</li>
            <li className="text__seo-li">Hide products.</li>
            <li className="text__seo-li">Update photos.</li>
            <li className="text__seo-li">Improve descriptions.</li>
            <li className="text__seo-li">Reorder categories.</li>
            <li className="text__seo-li">Publish daily offers.</li>
            <li className="text__seo-li">Manage seasonal dishes.</li>
          </ul>

          <h3>The menu should be editable in daily work</h3>

          <p>
            A digital menu is most useful when it can be changed by the people
            who actually work with the menu: the owner, manager or responsible
            staff member.
          </p>
        </section>

        <section id="costs" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Why fast price updates matter for restaurants</h2>

            <p>
              Restaurants work with changing purchase prices, staff costs, energy
              costs, and seasonal products. When costs change, menu prices,
              offer structure, or portions sometimes need to be adjusted.
            </p>

            <p>
              An editable online menu does not remove cost pressure. But it
              helps keep visible information current without starting a full
              print or file update process every time.
            </p>

            <div className="seoComparison">
              <div className="seoComparison__col">
                <h3>Slow update process</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">Price change is written down.</li>
                  <li className="text__seo-li">
                    PDF or print file needs to be edited.
                  </li>
                  <li className="text__seo-li">Someone has to upload it again.</li>
                  <li className="text__seo-li">
                    Old versions may still stay online.
                  </li>
                </ul>
              </div>

              <div className="seoComparison__col seoComparison__col--highlight">
                <h3>Better process</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">Open the menu admin area.</li>
                  <li className="text__seo-li">Change the price.</li>
                  <li className="text__seo-li">Save the update.</li>
                  <li className="text__seo-li">
                    The QR menu shows the current version.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section id="availability" className="acquaintance">
          <h2>Hide dishes when they are not available</h2>

          <p>
            In restaurant operations, not every item is available all the time.
            Ingredients can be missing, a dish can sell out, or an offer may
            apply only at certain times.
          </p>

          <div className="seoComparison">
            <div className="seoComparison__col">
              <h3>Static menu</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">
                  Sold-out dishes stay visible.
                </li>
                <li className="text__seo-li">
                  Guests order something unavailable.
                </li>
                <li className="text__seo-li">
                  Staff must explain and disappoint.
                </li>
                <li className="text__seo-li">Changes are slow.</li>
              </ul>
            </div>

            <div className="seoComparison__col seoComparison__col--highlight">
              <h3>Editable online menu</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">
                  Dishes can be hidden temporarily.
                </li>
                <li className="text__seo-li">
                  Availability stays more current.
                </li>
                <li className="text__seo-li">Fewer misunderstandings.</li>
                <li className="text__seo-li">
                  Items can be activated again later.
                </li>
              </ul>
            </div>
          </div>

          <p>
            The goal is not to replace service. The goal is to reduce avoidable
            disappointment before it happens.
          </p>
        </section>

        <section id="daily-menu" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Publish daily menus and seasonal offers faster</h2>

            <p>
              Daily specials, lunch offers, seasonal dishes, or short-term
              promotions are strongest when guests see them at the right moment.
            </p>

            <ol className="seoSteps">
              <li className="text__seo-li">Create daily dish.</li>
              <li className="text__seo-li">Add price.</li>
              <li className="text__seo-li">Add photo.</li>
              <li className="text__seo-li">Make it visible online.</li>
              <li className="text__seo-li">Hide it later.</li>
            </ol>

            <p>
              This can be especially useful for cafés, bistros, pizzerias, and
              restaurants with changing offers. The menu is not only displayed
              digitally — it is actively managed.
            </p>

            <div className="seoCompareSmall">
              <div>
                <strong>Related page</strong>
                <p>
                  <Link to="/en/create-digital-daily-menu">
                    Create a digital daily menu
                  </Link>
                </p>
              </div>

              <div>
                <strong>Related page</strong>
                <p>
                  <Link to="/en/change-menu-online">
                    Change menu online
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </section>

        <section id="photos" className="acquaintance">
          <h2>Improve photos and descriptions directly</h2>

          <p>
            Good photos and clear descriptions help guests understand a dish
            faster. This is especially important for new dishes, seasonal offers,
            sets, or items that need explanation.
          </p>

          <ul className="qrArticle__block-li">
            <li className="text__seo-li">Replace photo.</li>
            <li className="text__seo-li">Shorten description.</li>
            <li className="text__seo-li">Explain ingredients.</li>
            <li className="text__seo-li">Describe spice level.</li>
            <li className="text__seo-li">Show side dishes.</li>
            <li className="text__seo-li">Highlight recommendations.</li>
          </ul>

          <p>
            A digital menu should not be “finished” after the first upload. It
            should be able to improve as the restaurant learns what guests need.
          </p>

          <VisualIdea>
            A simple admin screen where the owner replaces a dish photo,
            shortens the description and checks the updated menu on a
            smartphone.
          </VisualIdea>
        </section>

        <section id="pdf" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Why PDF menus are difficult when changes happen often</h2>

            <p>
              A PDF can work for the beginning. But when prices, daily offers,
              or availability change regularly, a PDF becomes inconvenient.
            </p>

            <div className="seoComparison">
              <div className="seoComparison__col">
                <h3>PDF menu</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Every change creates a new file.
                  </li>
                  <li className="text__seo-li">
                    Old versions can keep circulating.
                  </li>
                  <li className="text__seo-li">
                    Often uncomfortable on smartphones.
                  </li>
                  <li className="text__seo-li">
                    No simple visibility logic for sold-out items.
                  </li>
                </ul>
              </div>

              <div className="seoComparison__col seoComparison__col--highlight">
                <h3>Online menu</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Changes happen in the management area.
                  </li>
                  <li className="text__seo-li">
                    Current version is reachable by QR code.
                  </li>
                  <li className="text__seo-li">Better mobile experience.</li>
                  <li className="text__seo-li">
                    Items can be visible or hidden.
                  </li>
                </ul>
              </div>
            </div>

            <p>
              If guests see the menu online before visiting, the visible menu
              should be as current as possible.
            </p>
          </div>
        </section>

        <section id="workflow" className="acquaintance">
          <h2>What a good menu management workflow looks like</h2>

          <p>
            Good menu management should be simple enough that it is actually
            used in everyday restaurant work.
          </p>

          <ol className="seoSteps">
            <li className="text__seo-li">
              Administrator opens the menu management area.
            </li>
            <li className="text__seo-li">
              Category or product is selected.
            </li>
            <li className="text__seo-li">
              Price, photo, or description is changed.
            </li>
            <li className="text__seo-li">
              Product is made visible, hidden, or updated.
            </li>
            <li className="text__seo-li">Change is saved.</li>
            <li className="text__seo-li">
              Guest sees the updated version through QR code or link.
            </li>
          </ol>

          <p>
            The workflow does not need to be complicated. The key is that
            changes should not be delayed just because the process is too hard.
          </p>
        </section>

        <section id="mistakes" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Common mistakes in online menu management</h2>

            <p>
              Online menu management only helps when the process is clear for
              the restaurant team. If editing is confusing, the menu will still
              become outdated.
            </p>

            <div className="seoComparison">
              <div className="seoComparison__col">
                <h3>Common mistakes</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Too many required fields for small changes.
                  </li>
                  <li className="text__seo-li">
                    Operators do not understand whether an item is public or
                    hidden.
                  </li>
                  <li className="text__seo-li">
                    Old prices stay online.
                  </li>
                  <li className="text__seo-li">
                    Poor or outdated photos remain visible.
                  </li>
                  <li className="text__seo-li">
                    Daily offers stay visible too long.
                  </li>
                  <li className="text__seo-li">
                    The public smartphone view is not checked after updates.
                  </li>
                </ul>
              </div>

              <div className="seoComparison__col seoComparison__col--highlight">
                <h3>Better</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Keep editing simple and fast.
                  </li>
                  <li className="text__seo-li">
                    Make visibility clear.
                  </li>
                  <li className="text__seo-li">
                    Update prices when they change.
                  </li>
                  <li className="text__seo-li">
                    Replace weak photos.
                  </li>
                  <li className="text__seo-li">
                    Remove or hide old daily offers.
                  </li>
                  <li className="text__seo-li">
                    Check the menu as guests see it.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <div id="bestplace" className="qrArticle__anchorTarget">
          <SeoWorkSteps lang="en" />
        </div>

        <section id="faq" className="acquaintance">
          <h2>Frequently asked questions about online menu management</h2>

          <div className="seoFaq">
            <details>
              <summary>Can I change prices myself?</summary>
              <p>
                Yes. This is one of the most important benefits of an editable
                online menu.
              </p>
            </details>

            <details>
              <summary>Can I hide sold-out dishes?</summary>
              <p>
                Yes. A good digital menu management system should let you hide
                products temporarily and activate them again later.
              </p>
            </details>

            <details>
              <summary>Is a PDF menu enough?</summary>
              <p>
                For a simple start, a PDF can be enough. If changes happen
                often, an editable online menu is usually more practical.
              </p>
            </details>

            <details>
              <summary>Do I need a developer for changes?</summary>
              <p>
                The goal of good menu management is that owners or
                administrators can make important changes themselves.
              </p>
            </details>

            <details>
              <summary>Can I change daily specials online?</summary>
              <p>
                Yes. Daily dishes, seasonal offers, or promotions are typical
                use cases for online menu management.
              </p>
            </details>
          </div>
        </section>
        <SeoRelatedLinks
          lang="en"
          group="menu"
          exclude="/en/manage-menu-online-for-restaurants"
        />
      </article>
    </main>
  );
}